import { type DocumentDetails } from "~/api/types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import i18n from "~/i18n";
import { z } from "zod";
import { type UpdateDocumentData } from "~/api/v2/requests";
import { isSameDateValue, toISOString } from "~/helpers/date";

interface Props {
  document: DocumentDetails;
}

export type DocumentEditFormValues = z.infer<typeof formSchema>;

const formSchema = z
  .object({
    ownerId: z.string({ message: i18n.t("validations.required") }),
    publisherId: z.string().optional(),
    categoryId: z.string({ message: i18n.t("validations.required") }),
    tierId: z.string({ message: i18n.t("validations.required") }),
    referenceNumber: z.string().trim().max(50).nullable(),
    tags: z.array(z.string()),
    scheduleReviewPeriod: z.string().nullable(),
    scheduleReviewFrom: z.date().nullable(),
    scheduleReviewTo: z.date().nullable(),
    parentDocuments: z.array(
      z.object({ value: z.string(), label: z.string() }),
    ),
    childDocuments: z.array(z.object({ value: z.string(), label: z.string() })),
  })
  .refine(
    (data) =>
      data.scheduleReviewPeriod || data.scheduleReviewTo
        ? data.scheduleReviewFrom !== null
        : true,
    {
      message: i18n.t("validations.startDateRequired"),
      path: ["scheduleReviewFrom"],
    },
  )
  .refine(
    (data) =>
      data.scheduleReviewFrom && data.scheduleReviewTo
        ? data.scheduleReviewTo > data.scheduleReviewFrom
        : true,
    {
      message: i18n.t("validations.endDateAfter"),
      path: ["scheduleReviewTo"],
    },
  );

const getDocumentEditFormDefaultValues = (
  document: DocumentDetails,
): DocumentEditFormValues => ({
  ownerId: document.owner.userId.toString(),
  publisherId: document.publisher?.userId.toString(),
  categoryId: document.category.id.toString(),
  tierId: document.tier.id.toString(),
  referenceNumber: document.referenceNumber ?? null,
  tags: document.tags.map((tag) => tag.id.toString()),
  scheduleReviewPeriod: document.scheduleReviewPeriod ?? null,
  scheduleReviewFrom: document.scheduleReviewFrom
    ? new Date(document.scheduleReviewFrom)
    : null,
  scheduleReviewTo: document.scheduleReviewTo
    ? new Date(document.scheduleReviewTo)
    : null,
  parentDocuments: document.parentDocuments.map((doc) => ({
    value: doc.id.toString(),
    label: doc.title,
  })),
  childDocuments: document.childDocuments.map((doc) => ({
    value: doc.id.toString(),
    label: doc.title,
  })),
});

export function useDocumentEditForm({ document }: Props) {
  const form = useForm<DocumentEditFormValues>({
    values: getDocumentEditFormDefaultValues(document),
    resolver: zodResolver(formSchema),
  });

  const filterUnchangedValues = (
    values: DocumentEditFormValues,
  ): UpdateDocumentData => {
    const result: UpdateDocumentData = {};

    // Category
    if (values.categoryId !== document.category.id.toString()) {
      result.categoryId = Number(values.categoryId);
    }

    // Tier
    if (values.tierId !== document.tier.id.toString()) {
      result.tierId = Number(values.tierId);
    }

    // Reference Number
    if (values.referenceNumber !== (document.referenceNumber ?? null)) {
      result.referenceNumber = values.referenceNumber;
    }

    // Owner
    if (values.ownerId !== document.owner.userId.toString()) {
      result.ownerId = Number(values.ownerId);
    }

    // Publisher
    if (values.publisherId !== document.publisher?.userId.toString()) {
      result.publisherId = values.publisherId
        ? Number(values.publisherId)
        : undefined;
    }

    // Schedule Review Dates
    const newScheduleFrom = toISOString(values.scheduleReviewFrom);
    if (!isSameDateValue(newScheduleFrom, document.scheduleReviewFrom)) {
      result.scheduleReviewFrom = newScheduleFrom;
    }

    const newScheduleTo = toISOString(values.scheduleReviewTo);
    if (!isSameDateValue(newScheduleTo, document.scheduleReviewTo)) {
      result.scheduleReviewTo = newScheduleTo;
    }

    if (values.scheduleReviewPeriod !== document.scheduleReviewPeriod) {
      result.scheduleReviewPeriod = values.scheduleReviewPeriod;
    }

    // Tags
    const newTags = values.tags.map((tag) => Number(tag));
    const currentTags = document.tags.map((tag) => tag.id);

    if (xor(newTags, currentTags).length > 0) {
      result.tagIds = newTags;
    }

    // Linked Documents
    const newLinkedDocuments = [
      ...values.parentDocuments.map((p) => Number(p.value)),
      ...values.childDocuments.map((c) => Number(c.value)),
    ];

    const currentLinkedDocuments = [
      ...document.parentDocuments.map((p) => p.id),
      ...document.childDocuments.map((c) => c.id),
    ];

    if (xor(newLinkedDocuments, currentLinkedDocuments).length > 0) {
      result.linkedDocumentIds = newLinkedDocuments;
    }

    return result;
  };

  return { form, filterUnchangedValues };
}

const xor = (a: number[], b: number[]) => {
  const setA = new Set(a);
  const setB = new Set(b);

  return [
    ...a.filter((value) => !setB.has(value)),
    ...b.filter((value) => !setA.has(value)),
  ];
};
